import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { whatsappAccountService } from '../../services/api';
import { INK, MUTED, RULE, ChartTooltip, axisProps, formatCount, prefersReducedMotion } from '../analytics/analyticsUi';

const SERIES = [
  { key: 'sent', label: 'Sent', color: '#9DB4D3' },
  { key: 'delivered', label: 'Delivered', color: '#128C7E' },
  { key: 'read', label: 'Read', color: '#1BAA52' },
  { key: 'clicked', label: 'Button clicks', color: '#7C6FD6' }
];
// Templates that were never approved have nothing to report
const REPORTABLE = (t) => !['REJECTED', 'PENDING', 'IN_APPEAL'].includes(t.status);

// Periods Meta can answer: it keeps template performance for 90 days
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
export const PERIODS = {
  '7d': { label: 'Last 7 days', range: () => [dateKey(daysAgo(6)), dateKey(new Date())] },
  '30d': { label: 'Last 30 days', range: () => [dateKey(daysAgo(29)), dateKey(new Date())] },
  '90d': { label: 'Last 90 days', range: () => [dateKey(daysAgo(89)), dateKey(new Date())] },
  month: {
    label: 'This month',
    range: () => { const d = new Date(); return [dateKey(new Date(d.getFullYear(), d.getMonth(), 1)), dateKey(d)]; }
  },
  lastMonth: {
    label: 'Last month',
    range: () => {
      const d = new Date();
      const start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const end = new Date(d.getFullYear(), d.getMonth(), 0);
      // Meta only answers the last 90 days; last month always fits
      return [dateKey(start), dateKey(end)];
    }
  }
};

const percent = (part, whole) => (whole ? (part / whole) * 100 : null);
const formatShare = (value) => (value === null ? '–' : `${value.toFixed(1)}%`);
const shortDay = (unix) => new Date(unix * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

const PeriodSelect = ({ value, onChange, id }) => (
  <FormControl size="small" sx={{ minWidth: 160 }}>
    <InputLabel id={id}>Period</InputLabel>
    <Select labelId={id} label="Period" value={value} onChange={(e) => onChange(e.target.value)}>
      {Object.entries(PERIODS).map(([key, p]) => <MenuItem key={key} value={key}>{p.label}</MenuItem>)}
    </Select>
  </FormControl>
);

const useTemplateAnalytics = (accountId, templateIds, period) => {
  const [state, setState] = useState({ loading: false, error: null, data: null });
  const idsKey = templateIds.join(',');
  useEffect(() => {
    if (!accountId || !idsKey) {
      setState({ loading: false, error: null, data: null });
      return undefined;
    }
    let cancelled = false;
    const [start, end] = PERIODS[period].range();
    setState((s) => ({ ...s, loading: true, error: null }));
    whatsappAccountService.getTemplatePerformance(accountId, { templateIds: idsKey, start, end })
      .then((response) => { if (!cancelled) setState({ loading: false, error: null, data: response.data }); })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, error: err.response?.data?.error || 'Performance could not be loaded from Meta. Try again.', data: null });
      });
    return () => { cancelled = true; };
  }, [accountId, idsKey, period]);
  return state;
};

// ---------------------------------------------------------------------------
// One template: figures, daily chart and clicks per button (in the detail panel)
// ---------------------------------------------------------------------------
export const TemplatePerformancePanel = ({ accountId, template }) => {
  const [period, setPeriod] = useState('30d');
  const reportable = REPORTABLE(template);
  const ids = useMemo(() => (reportable ? [template.id] : []), [reportable, template.id]);
  const { loading, error, data } = useTemplateAnalytics(accountId, ids, period);
  const row = data?.templates?.[0];

  if (!reportable) {
    return (
      <Typography variant="body2" sx={{ color: MUTED }}>
        Performance appears once the template is approved and sent.
      </Typography>
    );
  }

  const figures = row && [
    { label: 'Sent', value: formatCount(row.sent) },
    { label: 'Delivered', value: formatCount(row.delivered), hint: `${formatShare(percent(row.delivered, row.sent))} of sent` },
    { label: 'Read', value: formatCount(row.read), hint: `${formatShare(percent(row.read, row.delivered))} of delivered` },
    ...(row.buttons.length ? [{ label: 'Button clicks', value: formatCount(row.clicked), hint: `${formatShare(percent(row.clicked, row.delivered))} of delivered` }] : [])
  ];
  const chartData = (row?.daily || []).map((d) => ({ day: shortDay(d.start), ...d }));
  const series = SERIES.filter((s) => s.key !== 'clicked' || row?.buttons.length);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
        <Typography component="h3" sx={{ fontWeight: 600, color: INK }}>Performance</Typography>
        <PeriodSelect value={period} onChange={setPeriod} id={`perf-period-${template.id}`} />
      </Box>

      {error && <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !row ? (
        <Box aria-busy="true"><Skeleton height={64} /><Skeleton variant="rounded" height={160} /></Box>
      ) : row && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${figures.length}, minmax(0, 1fr))`, border: `1px solid ${RULE}`, borderRadius: 1.5, mb: 2 }}>
            {figures.map((f, i) => (
              <Box key={f.label} sx={{ px: 1.5, py: 1.25, borderLeft: i ? `1px solid ${RULE}` : 'none', minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: MUTED, display: 'block' }} noWrap>{f.label}</Typography>
                <Typography sx={{ color: INK, fontWeight: 600, fontSize: '1.15rem', fontVariantNumeric: 'tabular-nums' }}>{f.value}</Typography>
                {f.hint && <Typography variant="caption" sx={{ color: MUTED, display: 'block' }} noWrap>{f.hint}</Typography>}
              </Box>
            ))}
          </Box>

          {!row.sent ? (
            <Typography variant="body2" sx={{ color: MUTED }}>Not sent in this period.</Typography>
          ) : (
            <Box sx={{ height: 180 }} role="img" aria-label={`Daily sent, delivered and read messages for ${template.name}`}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={RULE} vertical={false} />
                  <XAxis dataKey="day" {...axisProps} minTickGap={20} />
                  <YAxis {...axisProps} axisLine={false} width={36} allowDecimals={false} tickFormatter={formatCount} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend iconType="plainline" wrapperStyle={{ fontSize: 12, color: MUTED }} />
                  {series.map((s) => (
                    <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2} dot={false} isAnimationActive={!prefersReducedMotion()} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}

          {row.buttons.length > 0 && row.sent > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: MUTED, fontWeight: 600, mb: 0.5 }}>Clicks by button</Typography>
              {row.buttons.map((b) => (
                <Box key={`${b.type}-${b.label}`} sx={{ py: 0.75, borderTop: `1px solid ${RULE}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: INK, overflowWrap: 'anywhere' }}>{b.label}</Typography>
                    <Typography variant="body2" sx={{ color: INK, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {formatCount(b.count)}
                      <Box component="span" sx={{ color: MUTED, ml: 1 }}>{formatShare(percent(b.count, row.clicked))}</Box>
                    </Typography>
                  </Box>
                  <Box sx={{ height: 4, bgcolor: RULE, borderRadius: 2, mt: 0.5 }} aria-hidden="true">
                    <Box sx={{ height: '100%', width: `${percent(b.count, row.clicked) || 0}%`, bgcolor: '#7C6FD6', borderRadius: 2 }} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1.5 }}>
            From Meta, in the account's timezone{data.timezone ? ` (${data.timezone})` : ''}. Meta keeps the last 90 days.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Every template of the account in one table
// ---------------------------------------------------------------------------
const COLUMNS = [
  { key: 'name', label: 'Template', align: 'left' },
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'deliveredRate', label: 'Delivery rate' },
  { key: 'read', label: 'Read' },
  { key: 'readRate', label: 'Read rate' },
  { key: 'clicked', label: 'Button clicks' },
  { key: 'clickRate', label: 'Click rate' }
];

export const TemplatePerformanceReport = ({ accountId, accountName, templates, categoryLabel, languageName, onOpen }) => {
  const [period, setPeriod] = useState('30d');
  const [onlySent, setOnlySent] = useState(true);
  const [sort, setSort] = useState({ key: 'sent', dir: 'desc' });
  const reportable = useMemo(() => templates.filter(REPORTABLE), [templates]);
  const ids = useMemo(() => reportable.map((t) => t.id), [reportable]);
  const { loading, error, data } = useTemplateAnalytics(accountId, ids, period);

  const rows = useMemo(() => {
    const stats = new Map((data?.templates || []).map((r) => [r.templateId, r]));
    return reportable
      .map((t) => {
        const s = stats.get(t.id) || { sent: 0, delivered: 0, read: 0, clicked: 0, buttons: [] };
        const hasButtons = (t.components || []).some((c) => c.type === 'BUTTONS');
        return {
          template: t,
          name: t.name,
          sent: s.sent,
          delivered: s.delivered,
          read: s.read,
          clicked: hasButtons ? s.clicked : null,
          deliveredRate: percent(s.delivered, s.sent),
          readRate: percent(s.read, s.delivered),
          clickRate: hasButtons ? percent(s.clicked, s.delivered) : null
        };
      })
      .filter((r) => !onlySent || r.sent > 0)
      .sort((a, b) => {
        const dir = sort.dir === 'asc' ? 1 : -1;
        if (sort.key === 'name') return dir * a.name.localeCompare(b.name);
        return dir * ((a[sort.key] ?? -1) - (b[sort.key] ?? -1));
      });
  }, [data, reportable, onlySent, sort]);

  const totals = rows.reduce((t, r) => ({
    sent: t.sent + r.sent,
    delivered: t.delivered + r.delivered,
    read: t.read + r.read,
    clicked: t.clicked + (r.clicked || 0)
  }), { sent: 0, delivered: 0, read: 0, clicked: 0 });

  const setSortKey = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const downloadCsv = () => {
    const [start, end] = PERIODS[period].range();
    const cell = (v) => { const t = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
    const rate = (v) => (v === null ? '' : v.toFixed(1));
    const lines = [
      ['Template', 'Language', 'Category', 'Template ID', 'Sent', 'Delivered', 'Delivery rate %', 'Read', 'Read rate %', 'Button clicks', 'Click rate %'].join(','),
      ...rows.map((r) => [r.name, r.template.language, categoryLabel(r.template.category), r.template.id, r.sent, r.delivered, rate(r.deliveredRate),
        r.read, rate(r.readRate), r.clicked ?? '', rate(r.clickRate)].map(cell).join(','))
    ];
    const blob = new Blob([`﻿${lines.join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `template-performance-${(accountName || 'account').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${start}-to-${end}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, px: { xs: 2, sm: 3 }, py: 2, borderBottom: `1px solid ${RULE}` }}>
        <PeriodSelect value={period} onChange={setPeriod} id="report-period" />
        <FormControlLabel
          control={<Switch size="small" checked={onlySent} onChange={(e) => setOnlySent(e.target.checked)} />}
          label={<Typography variant="body2" sx={{ color: INK }}>Only templates sent in this period</Typography>}
        />
        <Button
          startIcon={<DownloadIcon />}
          onClick={downloadCsv}
          disabled={loading || !rows.length}
          sx={{ textTransform: 'none', ml: { sm: 'auto' } }}
        >
          Download CSV
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

      {loading && !data ? (
        <Box sx={{ p: 3 }} aria-busy="true">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={40} />)}</Box>
      ) : !reportable.length ? (
        <Typography variant="body2" sx={{ color: MUTED, p: 3 }}>No approved templates yet.</Typography>
      ) : !rows.length ? (
        <Typography variant="body2" sx={{ color: MUTED, p: 3 }}>No templates were sent in this period.</Typography>
      ) : (
        <TableContainer sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
          <Table size="small" sx={{ minWidth: 860 }}>
            <TableHead>
              <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE, whiteSpace: 'nowrap' } }}>
                {COLUMNS.map((c, i) => (
                  <TableCell key={c.key} align={c.align || 'right'} sx={{ pl: i === 0 ? 3 : undefined, pr: i === COLUMNS.length - 1 ? 3 : undefined }} sortDirection={sort.key === c.key ? sort.dir : false}>
                    <TableSortLabel active={sort.key === c.key} direction={sort.key === c.key ? sort.dir : 'desc'} onClick={() => setSortKey(c.key)}>
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.template.id}
                  hover
                  onClick={() => onOpen(r.template)}
                  sx={{ cursor: 'pointer', '& td': { borderColor: RULE, fontVariantNumeric: 'tabular-nums' } }}
                >
                  <TableCell sx={{ pl: 3, maxWidth: 320 }}>
                    <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }} noWrap title={r.name}>{r.name}</Typography>
                    <Typography variant="caption" sx={{ color: MUTED }} noWrap>{languageName(r.template.language)}, {categoryLabel(r.template.category)}</Typography>
                  </TableCell>
                  <TableCell align="right">{formatCount(r.sent)}</TableCell>
                  <TableCell align="right">{formatCount(r.delivered)}</TableCell>
                  <TableCell align="right">{formatShare(r.deliveredRate)}</TableCell>
                  <TableCell align="right">{formatCount(r.read)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 48, height: 4, bgcolor: RULE, borderRadius: 2, display: { xs: 'none', md: 'block' } }} aria-hidden="true">
                        <Box sx={{ height: '100%', width: `${r.readRate || 0}%`, bgcolor: '#1BAA52', borderRadius: 2 }} />
                      </Box>
                      {formatShare(r.readRate)}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{r.clicked === null ? '–' : formatCount(r.clicked)}</TableCell>
                  <TableCell align="right" sx={{ pr: 3 }}>{formatShare(r.clickRate)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { borderColor: RULE, fontWeight: 600, color: INK, fontVariantNumeric: 'tabular-nums', borderTop: `2px solid ${RULE}` } }}>
                <TableCell sx={{ pl: 3 }}>All {formatCount(rows.length)} templates</TableCell>
                <TableCell align="right">{formatCount(totals.sent)}</TableCell>
                <TableCell align="right">{formatCount(totals.delivered)}</TableCell>
                <TableCell align="right">{formatShare(percent(totals.delivered, totals.sent))}</TableCell>
                <TableCell align="right">{formatCount(totals.read)}</TableCell>
                <TableCell align="right">{formatShare(percent(totals.read, totals.delivered))}</TableCell>
                <TableCell align="right">{formatCount(totals.clicked)}</TableCell>
                <TableCell align="right" sx={{ pr: 3 }}>–</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" sx={{ color: MUTED, display: 'block', px: 3, py: 1.5, borderTop: `1px solid ${RULE}` }}>
        Delivery rate is delivered of sent; read and click rates are of delivered messages. Read counts only people who have read receipts on.
        From Meta, which keeps the last 90 days.
      </Typography>
    </Paper>
  );
};
